import { useCallback, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { ApiError } from "@carepass/api-client";
import { Alert, Button, ChoiceControl, DetailList, Dialog } from "../../../ui";
import { HookFormField } from "../../../app/forms/HookFormField";
import { date, money, businessCodeLabel } from "../../workspace/model";
import { statusBadge } from "../../workspace/shared";
import type { Schemas } from "../../workspace/api.generated";
import type {
  MemberDetailPermissions,
  MemberDetailViewProps,
} from "./MemberDetailView";
import { terminationFormSchema, type TerminationFormValues } from "../schema";

export interface TerminationActions {
  readonly onVerifyTerminationAccount?: (input: {
    bankCode: string;
    accountNumber: string;
    quoteToken: string;
  }) => Promise<{ accountHolder: string; verificationToken: string }>;
  readonly onTerminationBanks?: () => Promise<
    readonly { code: string; name: string }[]
  >;
  readonly onCurrentTermination?: () => Promise<
    Schemas["RefundView"] | undefined
  >;
  readonly onRetryTermination?: (
    refundId: number,
  ) => Promise<Schemas["RefundView"]>;
}

type Props = TerminationActions & {
  detail: Schemas["MemberDetailView"];
  open: boolean;
  pending: boolean;
  permissions: MemberDetailPermissions;
  triggerRef: React.RefObject<HTMLButtonElement | null>;
  onClose: () => void;
  onResult: (result: Schemas["RefundView"]) => void;
  onTerminate: MemberDetailViewProps["onTerminate"];
  onPreview: MemberDetailViewProps["onPreviewTermination"];
  onRefresh: MemberDetailViewProps["onRefreshDetail"];
};

export function TerminationDialog(props: Props) {
  const { open, detail, onPreview, onCurrentTermination, onTerminationBanks } =
    props;
  const form = useForm<TerminationFormValues>({
    resolver: zodResolver(terminationFormSchema),
    defaultValues: {
      reason: "",
      bankCode: "",
      accountNumber: "",
      accountHolder: "",
      agreedToRefundPolicy: false,
      confirmed: false,
    },
  });
  const [preview, setPreview] = useState<Schemas["TerminationPreviewView"]>();
  const [current, setCurrent] = useState<Schemas["RefundView"]>();
  const [banks, setBanks] = useState<readonly { code: string; name: string }[]>(
    [],
  );
  const [verified, setVerified] = useState<{
    key: string;
    accountHolder: string;
    verificationToken: string;
  }>();
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [uncertain, setUncertain] = useState(false);
  const [error, setError] = useState("");
  const generation = useRef(0);
  const executing = useRef(false);
  const values = form.watch();
  const accountKey = [
    values.bankCode,
    values.accountNumber,
    preview?.quoteToken,
  ].join("|");
  const needsAccount = preview?.requiresBankAccount === true;
  const hasExecutionPermission =
    (current?.method ?? preview?.method) === "PG_CANCEL"
      ? props.permissions.cancelPayment === true
      : props.permissions.executeTransfer === true;
  const quoteReady = Boolean(
    preview?.allowed && preview.quoteToken?.length === 43 && !loading,
  );
  const validAccount = !needsAccount || verified?.key === accountKey;
  const canSubmit =
    quoteReady &&
    hasExecutionPermission &&
    validAccount &&
    values.reason.trim().length > 0 &&
    values.agreedToRefundPolicy &&
    values.confirmed &&
    !busy &&
    !props.pending &&
    !uncertain &&
    !current;

  const load = useCallback(async () => {
    const request = ++generation.current;
    setLoading(true);
    setError("");
    setPreview(undefined);
    setVerified(undefined);
    form.setValue("agreedToRefundPolicy", false);
    form.setValue("confirmed", false);
    try {
      const existing = await onCurrentTermination?.();
      if (request !== generation.current) return;
      setCurrent(existing);
      if (existing) {
        setUncertain(false);
        return;
      }
      const quote = await onPreview?.();
      if (
        !quote ||
        (quote.allowed &&
          (!quote.quoteToken ||
            quote.quoteToken.length !== 43 ||
            !quote.method ||
            [
              quote.grossAmount,
              quote.serviceUsageDeduction,
              quote.customerTotalRefundAmount,
            ].some(
              (value) => typeof value !== "number" || !Number.isFinite(value),
            )))
      )
        throw new Error("환불 견적을 조회하지 못했습니다.");
      if (request !== generation.current) return;
      setPreview(quote);
      if (quote.requiresBankAccount) {
        const options = await onTerminationBanks?.();
        if (request === generation.current) setBanks(options ?? []);
      }
    } catch (cause) {
      if (request === generation.current)
        setError(
          cause instanceof ApiError
            ? cause.message
            : "환불 견적을 불러오지 못했습니다. 다시 조회해 주세요.",
        );
    } finally {
      if (request === generation.current) setLoading(false);
    }
  }, [onPreview, onCurrentTermination, onTerminationBanks, form]);

  useEffect(() => {
    if (open) {
      form.reset();
      setCurrent(undefined);
      setUncertain(false);
      void load();
    }
    return () => {
      generation.current++;
    };
  }, [open, load, form]);

  const close = () => {
    if (busy || props.pending || verifying) return;
    props.onClose();
    window.requestAnimationFrame(() => props.triggerRef.current?.focus());
  };
  const verify = async () => {
    if (!props.onVerifyTerminationAccount || !preview?.quoteToken || verifying)
      return;
    setVerifying(true);
    setError("");
    setVerified(undefined);
    const key = accountKey;
    try {
      const result = await props.onVerifyTerminationAccount({
        bankCode: values.bankCode,
        accountNumber: values.accountNumber,
        quoteToken: preview.quoteToken,
      });
      setVerified({ ...result, key });
      form.setValue("accountHolder", result.accountHolder);
    } catch (cause) {
      setError(
        cause instanceof ApiError
          ? cause.message
          : "계좌를 검증하지 못했습니다. 다시 확인해 주세요.",
      );
    } finally {
      setVerifying(false);
    }
  };
  const execute = async (retry: boolean) => {
    if (executing.current || (!retry && !canSubmit)) return;
    executing.current = true;
    setBusy(true);
    setError("");
    try {
      const result =
        retry && current && props.onRetryTermination
          ? await props.onRetryTermination(current.refundId)
          : await props.onTerminate({
              ...form.getValues(),
              bankCode: needsAccount ? values.bankCode : null,
              accountNumber: needsAccount ? values.accountNumber : null,
              accountHolder: needsAccount ? verified!.accountHolder : null,
              accountVerificationToken: needsAccount
                ? verified!.verificationToken
                : null,
              quoteToken: preview!.quoteToken!,
            });
      setCurrent(result);
      props.onResult(result);
      await props.onRefresh();
      if (result.status === "COMPLETED") props.onClose();
    } catch (cause) {
      setUncertain(true);
      const message =
        cause instanceof ApiError
          ? cause.message
          : "처리 결과를 확인하지 못했습니다. 최신 상태를 조회해 주세요.";
      try {
        const latest = await props.onCurrentTermination?.();
        if (latest) {
          setCurrent(latest);
          setUncertain(false);
          await props.onRefresh();
        } else if (
          cause instanceof ApiError &&
          cause.kind !== "network" &&
          cause.kind !== "server"
        ) {
          setUncertain(false);
          await load();
        }
      } catch {
        /* Keep the uncertain state until a successful status read. */
      }
      setError(message);
    } finally {
      executing.current = false;
      setBusy(false);
    }
  };
  const retryAllowed =
    current &&
    ["FAILED", "REQUESTED"].includes(current.status) &&
    hasExecutionPermission &&
    !uncertain;
  return (
    <Dialog
      className="admin-member-termination"
      open={open}
      onClose={close}
      closeDisabled={busy || verifying || props.pending}
      title="서비스 해지를 진행할까요?"
      size="medium"
      tone="danger"
      primaryAction={
        current ? (
          <Button
            size="small"
            variant="danger"
            disabled={!retryAllowed}
            isLoading={busy}
            onClick={() => {
              void execute(true);
            }}
          >
            취소·환불 재처리
          </Button>
        ) : (
          <Button
            size="small"
            variant="danger"
            disabled={!canSubmit}
            isLoading={busy || props.pending}
            form="member-termination-form"
            type="submit"
          >
            해지 처리
          </Button>
        )
      }
      secondaryAction={
        <Button
          size="small"
          variant="quiet"
          disabled={busy || verifying}
          onClick={close}
        >
          {current ? "닫기" : "취소"}
        </Button>
      }
    >
      {loading ? (
        <p role="status">해지·환불 정보를 조회하는 중입니다.</p>
      ) : null}
      {error ? (
        <Alert
          persistent
          variant="danger"
          title="처리 상태 확인"
          action={
            <Button
              size="small"
              variant="quiet"
              disabled={busy}
              onClick={() => {
                void load();
              }}
            >
              최신 상태 조회
            </Button>
          }
        >
          {error}
        </Alert>
      ) : null}
      <section>
        <h3>회원 정보</h3>
        <DetailList
          items={[
            {
              label: "회원",
              value: [
                detail.profile.name ?? detail.profile.nameMasked,
                businessCodeLabel(detail.profile.telecomCode ?? ""),
                detail.profile.phoneNumber ?? detail.profile.phoneNumberMasked,
                detail.profile.birthDate,
              ]
                .filter(Boolean)
                .join(" · "),
            },
            {
              label: "요금제",
              value: [
                detail.planName,
                detail.monthlyFee == null ? null : money(detail.monthlyFee),
              ]
                .filter(Boolean)
                .join(" · "),
            },
            { label: "가입일", value: date(detail.profile.joinedAt) },
            {
              label: "가입채널",
              value: detail.channelName ?? detail.channelCode ?? "",
            },
          ]}
        />
      </section>
      {current ? (
        <Alert
          persistent
          title={current.status === "COMPLETED" ? "해지 완료" : "해지 처리 중"}
          variant={current.status === "COMPLETED" ? "success" : "warning"}
        >
          {statusBadge(current.status)} ·{" "}
          {current.method === "PG_CANCEL" ? "결제 취소" : "환불"}{" "}
          {money(current.customerTotalRefundAmount)}
          <br />
          {current.status === "FAILED"
            ? "취소·환불이 실패했습니다. 신규 이용과 정기결제는 중단된 상태이며, 재처리 성공 후 해지가 완료됩니다."
            : current.status === "COMPLETED"
              ? "취소·환불과 회원 해지가 완료되었습니다."
              : "취소·환불 완료 후 해지됩니다. 결과 확인 중에는 중복 실행할 수 없습니다."}
          <Button
            size="small"
            variant="quiet"
            disabled={busy}
            onClick={() => {
              void load();
            }}
          >
            최신 상태 조회
          </Button>
        </Alert>
      ) : (
        <form
          className="admin-command"
          id="member-termination-form"
          noValidate
          onSubmit={form.handleSubmit(() => execute(false))}
        >
          {preview && !preview.allowed ? (
            <Alert persistent variant="warning" title="현재 해지할 수 없습니다">
              {preview.activeTransactionCount > 0
                ? `진행 중인 거래 ${preview.activeTransactionCount}건을 먼저 완료하거나 취소해 주세요.`
                : preview.blockingReasonMessage}
            </Alert>
          ) : null}
          {quoteReady ? (
            <section>
              <h3>취소/환불 정보</h3>
              <DetailList
                items={[
                  {
                    label: "방식",
                    value:
                      preview?.method === "PG_CANCEL" ? "결제 취소" : "환불",
                  },
                  {
                    label: "금액",
                    value: money(preview?.customerTotalRefundAmount),
                  },
                  { label: "계산 근거", value: preview?.calculationText ?? "" },
                ]}
              />
              {!hasExecutionPermission ? (
                <Alert
                  persistent
                  variant="warning"
                  title="실행 권한이 없습니다"
                >
                  {preview?.method === "PG_CANCEL" ? "결제 취소" : "환불 송금"}{" "}
                  권한이 필요합니다.
                </Alert>
              ) : null}
              {needsAccount ? (
                <fieldset
                  className="admin-command__fieldset"
                  disabled={busy || verifying}
                >
                  <legend>환불 계좌</legend>
                  <HookFormField
                    compact
                    control="select"
                    formControl={form.control}
                    name="bankCode"
                    label="은행"
                    required
                  >
                    <option value="">은행 선택</option>
                    {banks.map((bank) => (
                      <option key={bank.code} value={bank.code}>
                        {bank.name}
                      </option>
                    ))}
                  </HookFormField>
                  <HookFormField
                    compact
                    formControl={form.control}
                    name="accountNumber"
                    label="계좌번호"
                    inputMode="numeric"
                    maxLength={20}
                    required
                  />
                  <Button
                    size="small"
                    variant="secondary"
                    disabled={
                      !hasExecutionPermission ||
                      !values.bankCode ||
                      !/^[0-9]{8,20}$/.test(values.accountNumber)
                    }
                    isLoading={verifying}
                    onClick={() => {
                      void verify();
                    }}
                  >
                    검증
                  </Button>
                  {verified?.key === accountKey ? (
                    <p role="status">
                      예금주 {verified.accountHolder} · 회원명 일치
                    </p>
                  ) : (
                    <p>회원 본인 명의의 계좌를 검증해 주세요.</p>
                  )}
                </fieldset>
              ) : null}
            </section>
          ) : null}
          <HookFormField
            compact
            control="textarea"
            formControl={form.control}
            name="reason"
            label="해지 사유"
            maxLength={1000}
            required
          />
          <ChoiceControl
            disabled={!quoteReady || busy}
            label="환불 정책과 공제 기준을 확인했습니다"
            {...form.register("agreedToRefundPolicy")}
          />
          <ChoiceControl
            disabled={!quoteReady || busy}
            label="신규 이용·정기결제 중단 및 취소·환불 성공 후 해지 완료를 확인했습니다"
            {...form.register("confirmed")}
          />
        </form>
      )}
    </Dialog>
  );
}
